/**
 * A bottom sheet with one set of choices (the PDApps window on a phone: what to show,
 * or how to sort — each pill opens its own). Slides up from the bottom; the rows are
 * radio rows the thumb can reach. A tap picks one and closes it; a tap outside or Escape
 * closes it without a change. (The phone's Back is not taken over: a pick can change the
 * address — the Recent page's filter — and a sheet entry in the history would undo it.)
 */
import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Check } from 'lucide-react'

export interface SheetOption {
  id: string
  label: string
  icon?: ReactNode
  count?: number
  selected: boolean
  onSelect: () => void
  testId?: string
}

export function FilterSheet({ title, options, themeClass, onClose }: {
  title: string
  options: SheetOption[]
  /** The theme's class — a portal sits outside the themed area (never on the backdrop). */
  themeClass: string
  onClose: () => void
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  // Once, as it opens: the current choice has the focus — and when it closes, focus goes
  // back to what opened it (the pill), never to the document.
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    panelRef.current?.querySelector<HTMLElement>('[aria-checked="true"]')?.focus({ preventScroll: true })
    return () => { if (opener?.isConnected) opener.focus({ preventScroll: true }) }
  }, [])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return }
      // Tab stays inside the sheet (it is modal).
      if (e.key !== 'Tab' || !panelRef.current) return
      const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>('button'))
      if (!items.length) return
      const i = items.indexOf(document.activeElement as HTMLElement)
      const next = e.shiftKey ? (i <= 0 ? items.length - 1 : i - 1) : (i === -1 || i === items.length - 1 ? 0 : i + 1)
      e.preventDefault()
      items[next].focus()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])
  return createPortal(
    <div className="pdc-sheet-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }} data-testid="apps-sheet">
      <div ref={panelRef} className={`pdc-sheet ${themeClass}`} role="dialog" aria-modal="true" aria-label={title}>
        <span className="pdc-sheet-handle" aria-hidden />
        <p className="pdc-sheet-title">{title}</p>
        <div className="pdc-sheet-group" role="radiogroup" aria-label={title}>
          {options.map(o => (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={o.selected}
              className="pdc-sheet-option"
              onClick={() => { o.onSelect(); onClose() }}
              data-testid={o.testId}
            >
              {o.icon}
              <span className="flex-1 text-left">{o.label}</span>
              {o.count !== undefined && <span className="themed-text-muted text-xs">{o.count}</span>}
              <Check size={16} className={o.selected ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  )
}
